import React, { useState } from 'react';

// One feature shown as a collage of the same screen on desktop, tablet and phone. It deliberately
// shows only part of the product: the desktop window is cropped and fades out at the bottom, and the
// tablet and phone overlap it, as SaaS landing pages do.
//
// `base` is a path without device or size suffix. The component loads, from /public/image/website:
//   <base>-desktop-1400.webp / -800.webp     (1440x900 viewport)
//   <base>-tablet-1000.webp  / -600.webp     (1180x820 viewport)
//   <base>-mobile-800.webp   / -400.webp     (390x844 viewport)
// They are produced by utilstop_apiengine/scripts/capture-marketing-shots.mjs from a demo dealership.
//
// A device whose image is missing shows a neutral skeleton. If the desktop image is missing and a
// `fallback` is given (the mock card this collage replaces), the fallback is rendered instead of the
// whole collage, so the page never shows a broken-image icon. `overlay` is drawn on top of the
// collage only while the desktop screenshot is showing.

export const DEVICE_SPECS = {
  desktop: { widths: [1400, 800], width: 1400, height: 875 },
  tablet: { widths: [1000, 600], width: 1000, height: 695 },
  mobile: { widths: [800, 400], width: 800, height: 1731 },
};

export const deviceFiles = (base, device) => DEVICE_SPECS[device].widths.map((w) => `${base}-${device}-${w}.webp`);

const FADE_MASK = 'linear-gradient(to bottom, #000 58%, transparent 100%)';

function Skeleton({ d }) {
  const bar = (w, o = 1) => ({ height: 8, width: w, borderRadius: 4, background: d.borderMd, opacity: o });
  return (
    <div style={{ position: 'absolute', inset: 0, padding: '8%', display: 'flex', flexDirection: 'column', gap: 10, background: d.bgAlt }} aria-hidden="true">
      <div style={{ display: 'flex', gap: 8 }}>
        {[0, 1, 2].map((i) => <div key={i} style={{ flex: 1, height: 34, borderRadius: 8, background: d.surface, border: `1px solid ${d.border}` }} />)}
      </div>
      {[90, 76, 84, 66, 74].map((w, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={bar('14%', 0.9)} /><div style={bar(`${w - 34}%`, 0.6)} /><div style={bar('10%', 0.4)} />
        </div>
      ))}
    </div>
  );
}

function Screen({ d, base, device, alt, sizes, eager, mask, onFail, failed, ratio }) {
  const spec = DEVICE_SPECS[device];
  const [big] = deviceFiles(base, device);
  const [wide, narrow] = spec.widths;
  return (
    <div style={{ position: 'relative', aspectRatio: ratio, background: d.bgAlt, overflow: 'hidden' }}>
      {failed ? <Skeleton d={d} /> : (
        <img
          src={big}
          srcSet={`${base}-${device}-${narrow}.webp ${narrow}w, ${base}-${device}-${wide}.webp ${wide}w`}
          sizes={sizes}
          width={spec.width}
          height={spec.height}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onError={onFail}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left', display: 'block',
            ...(mask ? { WebkitMaskImage: mask, maskImage: mask } : {}),
          }}
        />
      )}
    </div>
  );
}

export default function DeviceCollage({ d, base, alt, caption, eager = false, fallback = null, overlay = null, fade = true, style }) {
  const [failed, setFailed] = useState({});
  const fail = (k) => () => setFailed((f) => ({ ...f, [k]: true }));
  if (failed.desktop && fallback) return fallback;

  const bezel = '#1B1B1F';
  const dot = (c) => <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block' }} />;

  return (
    <figure style={{ margin: 0, position: 'relative', ...style }}>
      <div style={{ position: 'relative', width: '100%', paddingBottom: '19%' }}>
        {/* desktop */}
        <div style={{ width: '88%', borderRadius: 12, overflow: 'hidden', border: `1px solid ${d.borderMd}`, background: d.surface, boxShadow: d.sh8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 10px', background: d.bgAlt, borderBottom: `1px solid ${d.border}` }} aria-hidden="true">
            {dot('#FF5F57')}{dot('#FEBC2E')}{dot('#28C840')}
            <span style={{ marginLeft: 8, flex: 1, height: 16, borderRadius: 8, background: d.surface, border: `1px solid ${d.border}`, fontSize: 9, color: d.textMuted, display: 'flex', alignItems: 'center', padding: '0 9px' }}>app.ezeitpro.com</span>
          </div>
          <Screen d={d} base={base} device="desktop" alt={alt} sizes="(max-width: 768px) 90vw, 560px" eager={eager} mask={fade ? FADE_MASK : null} failed={failed.desktop} onFail={fail('desktop')} ratio="1400 / 875" />
        </div>

        {/* tablet */}
        <div style={{ position: 'absolute', right: 0, bottom: 0, width: '50%', borderRadius: 'clamp(8px, 2.4vw, 15px)', border: `clamp(3px, 0.9vw, 6px) solid ${bezel}`, background: bezel, boxShadow: d.sh8, overflow: 'hidden' }}>
          <div style={{ borderRadius: 'clamp(4px, 1.2vw, 8px)', overflow: 'hidden' }}>
            <Screen d={d} base={base} device="tablet" alt={`${alt} on a tablet`} sizes="(max-width: 768px) 48vw, 320px" eager={eager} failed={failed.tablet} onFail={fail('tablet')} ratio="1000 / 695" />
          </div>
        </div>

        {/* phone */}
        <div style={{ position: 'absolute', left: '3%', bottom: 0, width: '19%', borderRadius: 'clamp(9px, 2.6vw, 18px)', border: `clamp(3px, 0.8vw, 5px) solid ${bezel}`, background: bezel, boxShadow: d.sh8, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 3, left: '50%', transform: 'translateX(-50%)', width: '34%', height: 4, borderRadius: 3, background: '#000', zIndex: 2 }} aria-hidden="true" />
          <div style={{ borderRadius: 'clamp(6px, 1.9vw, 13px)', overflow: 'hidden' }}>
            <Screen d={d} base={base} device="mobile" alt={`${alt} on a phone`} sizes="(max-width: 768px) 20vw, 120px" eager={eager} failed={failed.mobile} onFail={fail('mobile')} ratio="800 / 1731" />
          </div>
        </div>
      </div>
      {!failed.desktop && overlay}
      {caption && <figcaption style={{ marginTop: 10, textAlign: 'center', fontSize: 12, color: d.textMuted, letterSpacing: '0.02em' }}>{caption}</figcaption>}
    </figure>
  );
}
